import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Plus, Search } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { usePurchaseRequisitions } from '@/hooks/use-procurement';
import { useRequisitionPermissions } from '@/hooks/use-requisition-permissions';
import { useOrgCurrency } from '@/hooks/use-settings';
import type { PurchaseRequisitionStatus } from '@/lib/types';
import { formatMoney } from '@/lib/currency';
import { formatDateOnly } from '@/lib/date-utils';
import {
  RequisitionPriorityBadge,
  RequisitionStatusBadge,
  estimatedTotal,
  statusLabels,
} from './requisition-shared';

const PAGE_SIZE = 20;

export function RequisitionsListPage() {
  const { canRaise, canReview } = useRequisitionPermissions();
  const orgCurrency = useOrgCurrency();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PurchaseRequisitionStatus | ''>('');
  const [mine, setMine] = useState(false);
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = usePurchaseRequisitions({
    q: search,
    status,
    mine: mine ? true : '',
    page,
    page_size: PAGE_SIZE,
  });
  const { data: pendingData } = usePurchaseRequisitions(
    { status: 'submitted', page_size: 1 },
    canReview
  );

  const requisitions = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const pendingCount = pendingData?.count ?? 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Requisitions</h1>
          <p className="text-gray-600 mt-1">
            Request gear and materials for approval
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        {canRaise && (
          <Button asChild>
            <Link to="/requisitions/new">
              <Plus className="h-4 w-4 mr-2" />
              New Requisition
            </Link>
          </Button>
        )}
      </div>

      {canReview && pendingCount > 0 && status !== 'submitted' && (
        <button
          type="button"
          onClick={() => {
            setStatus('submitted');
            setPage(1);
          }}
          className="w-full text-left rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 hover:bg-amber-100 transition-colors"
        >
          <span className="font-semibold">{pendingCount}</span>{' '}
          requisition{pendingCount === 1 ? '' : 's'} awaiting your approval — click to review.
        </button>
      )}

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by description..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as PurchaseRequisitionStatus | '');
                setPage(1);
              }}
              className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
            >
              <option value="">All statuses</option>
              {(Object.keys(statusLabels) as PurchaseRequisitionStatus[]).map((s) => (
                <option key={s} value={s}>
                  {statusLabels[s]}
                </option>
              ))}
            </select>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={mine}
                onChange={(e) => {
                  setMine(e.target.checked);
                  setPage(1);
                }}
                className="h-4 w-4 rounded border-gray-300"
              />
              Only mine
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-red-600">
              Failed to load requisitions. Please try again.
            </div>
          ) : requisitions.length === 0 ? (
            <div className="p-12 text-center">
              <ClipboardList className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No requisitions found.</p>
              {canRaise && (
                <Button asChild className="mt-4">
                  <Link to="/requisitions/new">Raise your first requisition</Link>
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Requested By</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Est. Total</TableHead>
                  <TableHead>Needed By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requisitions.map((req) => (
                  <TableRow key={req.id}>
                    <TableCell className="font-mono text-sm">
                      <Link
                        to={`/requisitions/${req.id}`}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        PR-{req.id}
                      </Link>
                    </TableCell>
                    <TableCell className="text-gray-900 max-w-xs truncate">
                      <Link to={`/requisitions/${req.id}`} className="hover:underline">
                        {req.description || '(no description)'}
                      </Link>
                      <div className="text-xs text-gray-500">
                        {req.lines.length} line{req.lines.length === 1 ? '' : 's'}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-700">{req.project_name || '—'}</TableCell>
                    <TableCell className="text-gray-700">
                      {req.requested_by_name || req.requested_by_username || '—'}
                    </TableCell>
                    <TableCell>
                      <RequisitionPriorityBadge priority={req.priority} />
                    </TableCell>
                    <TableCell>
                      <RequisitionStatusBadge status={req.status} />
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatMoney(estimatedTotal(req.lines), orgCurrency)}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {formatDateOnly(req.needed_by)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalCount > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of{' '}
            {totalCount}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!data?.previous}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={!data?.next}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
